import { useEffect, useState } from "react";

type FilePreviewProps = {
  src?: string;
  title?: string;
};

const getUrlPath = (value: string) => {
  try {
    return new URL(value).pathname.toLowerCase();
  } catch {
    return value.split("?")[0].split("#")[0].toLowerCase();
  }
};

const isImageFile = (value: string) => {
  const path = getUrlPath(value);
  return /\.(png|jpg|jpeg|webp|gif)$/i.test(path) || value.startsWith("data:image/");
};

const isPdfFile = (value: string) => {
  const path = getUrlPath(value);
  return path.endsWith(".pdf") || value.startsWith("data:application/pdf");
};

export function FilePreview({ src, title = "Document Preview" }: FilePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  if (!src) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-700">
        No file available
      </div>
    );
  }

  if (isImageFile(src)) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-linear-to-b from-slate-100 to-white">
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-sm">
            <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-emerald-600" />
            <p className="text-sm font-medium text-slate-700">Loading preview...</p>
          </div>
        ) : null}

        {hasError ? (
          <div className="flex h-[50vh] items-center justify-center p-6 text-center text-sm text-red-600">
            Unable to load image preview.
          </div>
        ) : (
          <img
            src={src}
            alt={title}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
            className="h-[50vh] w-full object-contain"
          />
        )}
      </div>
    );
  }

  if (isPdfFile(src)) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm">
            <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-emerald-600" />
            <p className="text-sm font-medium text-slate-700">Loading PDF...</p>
          </div>
        ) : null}

        <iframe
          src={src}
          title={title}
          onLoad={() => setIsLoading(false)}
          className="h-[78vh] w-full"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
      >
        Open File in New Tab
      </a>
    </div>
  );
}
